import React from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import get from 'lodash/get';

import Link from '@nerdwallet/react-link';
import Button from '@nerdwallet/react-button';
import AuthFlowButton from '@nerdwallet/react-auth-flow-button';
import AppButtons from '../app-buttons/app-buttons';

import {
  repeatable,
  stringInputSpec,
  stringTextAreaSpec,
  stringSelectSpec,
} from '../../lib/editing-configs';

export const NULL_BUTTON_TYPE = 'none';
export const CUSTOM_BUTTON_TYPE = 'custom';
const APP_STORE_BUTTON_TYPE = 'textMeApp';

const SmartButton = ({
  buttonClass,
  buttonType,
  buttonText,
  buttonURL,
  successURL,
  authDialogCopyOverrides,
  authDialogImageOverrides,
  appStoreURL,
  googlePlayURL,
  buttonParams,
  displayBlock,
  link,
  primary,
}) => {
  if (buttonType === NULL_BUTTON_TYPE) {
    return null;
  }

  const params = buttonParams.reduce(
    (paramDict, { queryKey, queryValue }) => ({
      ...paramDict,
      [queryKey]: queryValue,
    }),
    {}
  );

  if (buttonURL) {
    // Has user already included a query in their buttonURL?
    const [url, existingQuery = ''] = buttonURL.split('?');
    const newQuery =
      buttonParams.length > 0
        ? `?${existingQuery}&${queryString.stringify(params)}`
        : existingQuery && `?${existingQuery}`;
    const href = `${url}${newQuery}`;

    return link ? (
      <Link href={href} className={buttonClass}>
        {buttonText}
      </Link>
    ) : (
      <Button
        primary={primary}
        className={buttonClass}
        href={href}
        block={displayBlock}
      >
        {buttonText}
      </Button>
    );
  }

  if (buttonType === APP_STORE_BUTTON_TYPE) {
    return (
      <AppButtons appStoreURL={appStoreURL} googlePlayURL={googlePlayURL} />
    );
  }

  const flowProps = {
    trk: params,
    flowConfig: {},
  };

  if (successURL) {
    flowProps.onSuccess = successURL;
  }

  if (authDialogCopyOverrides) {
    flowProps.flowConfig.copyOverrides = {};
    authDialogCopyOverrides.forEach((copyOverride) => {
      flowProps.flowConfig.copyOverrides[copyOverride.copyKey] =
        copyOverride.copyValue;
    });
  }

  if (authDialogImageOverrides) {
    flowProps.flowConfig.imageOverrides = {};
    authDialogImageOverrides.forEach((imageOverride) => {
      flowProps.flowConfig.imageOverrides[imageOverride.imageKey] =
        imageOverride.imageValue;
    });
  }

  return (
    <AuthFlowButton
      flowPreset={buttonType}
      flowProps={flowProps}
      className={buttonClass}
      block={displayBlock}
    >
      {buttonText}
    </AuthFlowButton>
  );
};

SmartButton.displayName = 'SmartButton';

SmartButton.propTypes = {
  buttonClass: PropTypes.string,
  buttonType: PropTypes.string,
  buttonText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  buttonURL: PropTypes.string,
  successURL: PropTypes.string,
  authDialogCopyOverrides: PropTypes.arrayOf(
    PropTypes.shape({
      copyKey: PropTypes.string,
      copyValue: PropTypes.string,
    })
  ),
  authDialogImageOverrides: PropTypes.arrayOf(
    PropTypes.shape({
      imageKey: PropTypes.string,
      imageValue: PropTypes.string,
    })
  ),
  buttonParams: PropTypes.array,
  displayBlock: PropTypes.bool,
  link: PropTypes.bool,
  primary: PropTypes.bool,
  appStoreURL: PropTypes.string,
  googlePlayURL: PropTypes.string,
};

SmartButton.defaultProps = {
  buttonType: NULL_BUTTON_TYPE,
  buttonParams: [],
  displayBlock: false,
  link: false,
  primary: true,
};

SmartButton.getEditingConfig = (componentConfig) => {
  const buttonType = get(componentConfig, 'props.buttonType');
  const isAuthDialog =
    buttonType &&
    buttonType !== NULL_BUTTON_TYPE &&
    buttonType !== CUSTOM_BUTTON_TYPE &&
    buttonType !== APP_STORE_BUTTON_TYPE;
  const successURLSpec = isAuthDialog
    ? [
        stringInputSpec('successURL', 'CTA on success url', {
          help:
            'For Dialog CTAs, the URL to take the user after they have successfully registered',
        }),
      ]
    : [];

  const authDialogCopyOverridesSpec = isAuthDialog
    ? [
        repeatable(
          'authDialogCopyOverrides',
          'Dialog copy overrides',
          'Param',
          () => [
            stringInputSpec('copyKey', 'Key'),
            stringTextAreaSpec('copyValue', 'Value'),
          ]
        ),
      ]
    : [];

  const authDialogImageOverridesSpec = isAuthDialog
    ? [
        repeatable(
          'authDialogImageOverrides',
          'Dialog image overrides',
          'Param',
          () => [
            stringInputSpec('imageKey', 'Key'),
            stringTextAreaSpec('imageValue', 'Value'),
          ]
        ),
      ]
    : [];

  const appStoreUrlOverridesSpec =
    buttonType === APP_STORE_BUTTON_TYPE ? AppButtons.getEditingConfig() : [];

  return [
    stringSelectSpec('buttonType', 'CTA', [
      {
        label: 'None',
        value: NULL_BUTTON_TYPE,
      },
      {
        label: 'Custom URL',
        value: CUSTOM_BUTTON_TYPE,
      },
      {
        label: 'Dialog: Generic Onboarding',
        value: 'onboardingGeneric',
      },
      {
        label: 'Dialog: Connect Accounts',
        value: 'connectAccounts',
      },
      {
        label: 'Dialog: Credit Score',
        value: 'creditScore',
      },
      {
        label: 'Dialog: BillShark',
        value: 'bills',
      },
      {
        label: 'Dialog: Emergency Fund Goal',
        value: 'emergencyFundGoal',
      },
      {
        label: 'Dialog: Savings Goal',
        value: 'savingsGoal',
      },
      {
        label: 'Dialog: Retirement Goal',
        value: 'retirementGoal',
      },
      {
        label: 'Dialog: Debt Goal',
        value: 'debtGoal',
      },
      {
        label: 'Dialog: Credit Score Goal',
        value: 'creditGoal',
      },
      {
        label: 'Dialog: Cash Back',
        value: 'cashBack',
      },
      {
        label: 'App Download Buttons',
        value: APP_STORE_BUTTON_TYPE,
      },
    ]),
    stringTextAreaSpec('buttonText', 'CTA Text'),
    ...successURLSpec,
    ...authDialogCopyOverridesSpec,
    ...authDialogImageOverridesSpec,
    ...appStoreUrlOverridesSpec,
    stringTextAreaSpec('buttonURL', 'Custom button target url', {
      help:
        'URL for the user to be redirected to on click. Be sure to add tracking params (e.g. www.a.com?trk_app=landing-page-client)',
    }),
    repeatable('buttonParams', 'Tracking Params', 'Param', () => [
      stringInputSpec('queryKey', 'Key'),
      stringTextAreaSpec('queryValue', 'Value'),
    ]),
  ];
};

export default SmartButton;
